import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MessageCircle, X, ChevronLeft } from 'lucide-react';
import ChatConversation from './ChatConversation';
import { useOnlineIds } from './OnlineUsers';

const FloatingMessenger = () => {
  const { API, user } = useContext(AuthContext);
  const onlineIds = useOnlineIds();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [conversations, setConversations] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [chatUser, setChatUser] = useState(null);
  const wrapRef = useRef(null);

  const authHeaders = () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  useEffect(() => {
    if (!user) return undefined;
    const fetchUnread = async () => {
      try {
        const r = await axios.get(`${API}/messages/unread-count`, authHeaders());
        setUnread(r.data?.unread_count || 0);
      } catch { /* silent */ }
    };
    fetchUnread();
    const i = setInterval(fetchUnread, 30000);
    return () => clearInterval(i);
  }, [API, user]);

  // Close when clicking anywhere outside the panel/button
  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('touchstart', onOutside);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('touchstart', onOutside);
    };
  }, [open]);

  const fetchPanelData = async () => {
    try {
      const [convRes, onlineRes] = await Promise.all([
        axios.get(`${API}/messages/conversations`, authHeaders()),
        axios.get(`${API}/presence/online`, authHeaders()),
      ]);
      setConversations(convRes.data.conversations || []);
      setOnlineUsers((onlineRes.data.online || []).filter((u) => u.id !== user.id));
    } catch { /* silent */ }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchPanelData();
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric' });
  };

  if (!user) return null;

  return (
    <div ref={wrapRef}>
      {/* Floating button - bottom right */}
      <button
        onClick={toggle}
        data-testid="messenger-float-btn"
        aria-label="צ'אטים"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 md:w-16 md:h-16 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 group"
        style={{ background: 'linear-gradient(135deg, #1d6ff2 0%, #1e2b5e 100%)' }}
      >
        {open ? (
          <X className="w-7 h-7 text-white" />
        ) : (
          <MessageCircle className="w-7 h-7 md:w-8 md:h-8 text-white" />
        )}
        <span
          className="absolute -top-7 left-1/2 -translate-x-1/2 bg-white/95 dark:bg-slate-800 text-[#1d6ff2] dark:text-[#6ea8ff] text-[11px] font-black px-2.5 py-0.5 rounded-full shadow-md border border-blue-200 dark:border-blue-900 whitespace-nowrap pointer-events-none"
          data-testid="messenger-float-label"
        >
          צ'אטים
        </span>
        {!open && unread > 0 && (
          <span
            data-testid="messenger-float-badge"
            className="absolute -top-1 -left-1 min-w-[22px] h-[22px] px-1.5 bg-red-500 text-white text-xs font-black rounded-full flex items-center justify-center ring-2 ring-white"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
        <div className="absolute left-full ml-3 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none hidden md:block">
          הצ'אטים שלי
        </div>
      </button>

      {/* Messenger popup panel */}
      {open && (
        <div
          data-testid="messenger-panel"
          dir="rtl"
          className="fixed bottom-24 right-4 z-50 w-[calc(100vw-2rem)] max-w-[340px] bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden brand-font animate-in slide-in-from-bottom-4 duration-300"
        >
          <div
            className="px-4 py-3 text-white flex items-center justify-between"
            style={{ background: 'linear-gradient(135deg, #1e2b5e 0%, #17224d 60%, #10203f 100%)' }}
          >
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-cyan-300" />
              <span className="font-bold">הצ'אטים שלי</span>
              {unread > 0 && (
                <span className="min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-[11px] font-black rounded-full flex items-center justify-center">
                  {unread}
                </span>
              )}
            </div>
            <Link
              to="/messages"
              onClick={() => setOpen(false)}
              className="text-xs font-bold text-cyan-300 hover:text-white flex items-center gap-0.5 transition-colors"
              data-testid="messenger-all-link"
            >
              כל ההודעות
              <ChevronLeft className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="max-h-80 overflow-y-auto p-2">
            {/* Online users row */}
            {onlineUsers.length > 0 && (
              <div className="px-2 pt-1 pb-2 border-b border-slate-100 dark:border-slate-800 mb-1" data-testid="messenger-online-row">
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#10b47e] opacity-60"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#10b47e]"></span>
                  </span>
                  <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400">מחוברים עכשיו ({onlineUsers.length})</span>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {onlineUsers.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => { setChatUser({ id: u.id, name: u.full_name }); setOpen(false); }}
                      data-testid={`messenger-online-${u.id}`}
                      className="flex flex-col items-center gap-1 shrink-0 w-14 group"
                      title={`שלח הודעה ל${u.full_name}`}
                    >
                      <div className="relative">
                        <div className="rounded-full p-[2px] bg-gradient-to-br from-[#10b47e] to-emerald-400 group-hover:scale-105 transition-transform">
                          <Avatar className="w-10 h-10 border-2 border-white dark:border-slate-900">
                            {u.avatar && <AvatarImage src={u.avatar} alt={u.full_name} />}
                            <AvatarFallback className="bg-gradient-to-br from-[#1d6ff2] to-cyan-400 text-white font-bold text-sm">
                              {u.full_name?.charAt(0) || '?'}
                            </AvatarFallback>
                          </Avatar>
                        </div>
                        <span className="absolute bottom-0 left-0 w-3 h-3 bg-[#10b47e] rounded-full border-2 border-white dark:border-slate-900"></span>
                      </div>
                      <span className="text-[10px] font-semibold text-slate-600 dark:text-slate-300 truncate w-full text-center">
                        {u.full_name?.split(' ')[0]}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {conversations.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-sm">
                אין שיחות עדיין 🎾
              </div>
            ) : (
              conversations.map((c) => (
                <button
                  key={c.user_id}
                  onClick={() => { setChatUser({ id: c.user_id, name: c.name }); setOpen(false); }}
                  data-testid={`messenger-conv-${c.user_id}`}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-slate-100 dark:hover:bg-slate-800/70 transition-colors text-right"
                >
                  <div className="relative shrink-0">
                    <Avatar className="w-11 h-11 border border-slate-100 dark:border-slate-800">
                      {c.avatar && <AvatarImage src={c.avatar} alt={c.name} />}
                      <AvatarFallback className="bg-gradient-to-br from-[#1d6ff2] to-cyan-400 text-white font-bold">
                        {c.name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    {onlineIds.has(c.user_id) && (
                      <span className="absolute bottom-0 left-0 w-3 h-3 bg-[#10b47e] rounded-full border-2 border-white dark:border-slate-900"></span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`truncate text-sm ${c.unread > 0 ? 'font-black text-[#1e2b5e] dark:text-white' : 'font-bold text-slate-800 dark:text-slate-100'}`}>
                        {c.name}
                      </span>
                      <span className="text-[10px] text-slate-400 shrink-0">{formatTime(c.last_at)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className={`truncate text-xs ${c.unread > 0 ? 'font-bold text-slate-800 dark:text-slate-200' : 'text-slate-500'}`}>
                        {c.last_is_mine ? 'אתה: ' : ''}{c.last_message}
                      </span>
                      {c.unread > 0 && (
                        <span className="shrink-0 min-w-[18px] h-[18px] px-1 bg-[#1d6ff2] text-white text-[10px] font-black rounded-full flex items-center justify-center">
                          {c.unread}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Chat dialog */}
      {chatUser && (
        <ChatConversation
          open={!!chatUser}
          onClose={() => setChatUser(null)}
          otherUserId={chatUser.id}
          otherUserName={chatUser.name}
        />
      )}
    </div>
  );
};

export default FloatingMessenger;
